import React, { useState, useMemo } from 'react';
import { Box, Typography, Grid, Paper, Divider, Stack, TextField, Button, InputAdornment, IconButton, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import { useSelector } from 'react-redux';

const PanelAdmin = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    const customers = useSelector((state) => state.customers.data);
    const traceability = useSelector((state) => state.traceability.data);

    const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const xLabels = months;

    const allCustomerLastTraceDate = useMemo(() => {
        const traces = {};
        traceability.filter((t) => t.credit_id == null).forEach((t) => {
            const customerId = t.customer_id;
            if (!traces[customerId] || new Date(t.date) > new Date(traces[customerId])) {
                traces[customerId] = t.date;
            }
        });
        return traces;
    }, [traceability]);

    const filteredCustomersSet = useMemo(() => {
        if (!startDate && !endDate) return null;
        const custSet = new Set();
        traceability
            .filter((t) => t.credit_id == null)
            .forEach((t) => {
                const tDate = new Date(t.date);
                let valid = true;
                if (startDate) valid = valid && tDate >= new Date(startDate + 'T00:00:00');
                if (endDate) valid = valid && tDate <= new Date(endDate + 'T23:59:59');
                if (valid) custSet.add(t.customer_id);
            });
        return custSet;
    }, [traceability, startDate, endDate]);

    // Clientes activos según el filtro de fechas (o todos si no hay filtro)
    const activeCustomers = useMemo(() => {
        if (!filteredCustomersSet) return customers;
        return customers.filter(c => filteredCustomersSet.has(c.id_customer));
    }, [customers, filteredCustomersSet]);

    // Estadísticas mensuales: usa siempre el historial completo de fechas para el eje X
    const getMonthlyStats = (statusId) => {
        const stats = new Array(12).fill(0);
        activeCustomers.forEach((c) => {
            if (c.status_id === statusId) {
                const dateStr = allCustomerLastTraceDate[c.id_customer];
                if (dateStr) {
                    const monthIndex = new Date(dateStr).getMonth();
                    stats[monthIndex]++;
                }
            }
        });

        return stats;
    };

    const lineDataAprov = getMonthlyStats(5);
    const lineDataNoContesta = getMonthlyStats(9);
    const lineDataInterest = getMonthlyStats(4);

    const countAprov = activeCustomers.filter((c) => c.status_id === 5).length;
    const countNoContacted = activeCustomers.filter((c) => c.status_id === 9).length;
    const countInterested = activeCustomers.filter((c) => c.status_id === 4).length;
    const countContacted = activeCustomers.filter((c) => c.status_id === 12).length;
    const countNoInterested = activeCustomers.filter((c) => c.status_id === 11).length;

    const totalCustomers = activeCustomers.length || 1;
    const percentAprov = ((countAprov / totalCustomers) * 100).toFixed(1);
    const percentNoContesta = ((countNoContacted / totalCustomers) * 100).toFixed(1);
    const percentInteresado = ((countInterested / totalCustomers) * 100).toFixed(1);

    const pieData = [
        { id: 0, value: countContacted, color: '#0051b3' },
        { id: 1, value: countNoContacted, color: '#b39e00' },
    ];

    const theme = {
        primary: '#4f46e5',
        success: '#10b981',
        error: '#f43f5e',
        warning: '#f59e0b',
        textPrimary: '#0f172a',
        textSecondary: '#64748b',
        bg: 'linear-gradient(135deg, #b7daff20 20% )'
    };

    const cardStyle = {
        p: 3,
        height: 'auto',
        borderRadius: '24px',
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            boxShadow: '0 25px 30px -5px rgb(0 0 0 / 0.08)',
            transform: 'translateY(-2px)',
        },
    };

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, background: theme.bg, minHeight: '100vh', fontFamily: "'Inter', sans-serif", borderRadius: '24px' }}>
            <Box
                sx={{
                    mb: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', lg: 'center' },
                    gap: 3,
                    pb: 3,
                    borderBottom: '1px solid rgba(85, 85, 85, 1)'
                }}
            >
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{
                                width: 12,
                                height: 12,
                                background: `linear-gradient(135deg, ${theme.primary}, #818cf8)`,
                                borderRadius: '4px',
                                zIndex: 2
                            }} />
                            <Box sx={{
                                position: 'absolute',
                                width: 12,
                                height: 12,
                                borderRadius: '4px',
                                bgcolor: theme.primary,
                                opacity: 0.4,
                                animation: 'pulse 2s infinite ease-in-out',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)', opacity: 0.4 },
                                    '70%': { transform: 'scale(2.5)', opacity: 0 },
                                    '100%': { transform: 'scale(1)', opacity: 0 },
                                },
                            }} />
                        </Box>

                        <Typography
                            variant="overline"
                            sx={{
                                color: theme.textSecondary,
                                fontWeight: 800,
                                letterSpacing: 2,
                                textTransform: 'uppercase',
                                fontSize: '1rem'
                            }}
                        >
                            Dashboard <span style={{ color: theme.error }}>•</span> En Vivo
                        </Typography>
                    </Stack>

                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

                    <Paper sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
                        <FilterAltIcon sx={{ color: theme.textSecondary, ml: 1, mr: 1 }} />
                        <TextField
                            type="date"
                            size="small"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& fieldset': { border: 'none' } } }}
                        />
                        <Typography sx={{ color: theme.textSecondary, mx: 1 }}>-</Typography>
                        <TextField
                            type="date"
                            size="small"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& fieldset': { border: 'none' } } }}
                        />
                        {(startDate || endDate) && (
                            <IconButton size="small" onClick={() => { setStartDate(''); setEndDate(''); }} sx={{ color: theme.error, ml: 1 }}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Paper>

                    <Box sx={{
                        bgcolor: 'white',
                        px: 3,
                        py: 1.5,
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}>
                        <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 800, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Periodo Actual
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 800 }}>
                            {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} lg={12} >
                    <Grid container spacing={3}>
                        <StatCard
                            title="Aprobados"
                            count={countAprov}
                            percent={percentAprov}
                            data={lineDataAprov}
                            color={theme.success}
                            icon={<CheckCircleIcon />}
                            labels={xLabels}
                        />
                        <StatCard
                            title="No Contesta"
                            count={countNoContacted}
                            percent={percentNoContesta}
                            data={lineDataNoContesta}
                            color={theme.error}
                            icon={<CancelIcon />}
                            labels={xLabels}
                        />
                        <StatCard
                            title="Interesados"
                            count={countInterested}
                            percent={percentInteresado}
                            data={lineDataInterest}
                            color={theme.warning}
                            icon={<InfoIcon />}
                            labels={xLabels}
                        />
                    </Grid>
                    <Paper sx={{ ...cardStyle, position: 'relative', overflow: 'hidden', mt: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 4 }}>Distribución de Contacto</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: 3,
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <PieChart
                                    series={[{
                                        data: pieData,
                                        innerRadius: isMobile ? 55 : 80,
                                        outerRadius: isMobile ? 80 : 110,
                                        paddingAngle: 8,
                                        cornerRadius: 12,
                                    }]}
                                    width={isMobile ? 200 : 280}
                                    height={isMobile ? 200 : 280}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1 }}>{countContacted + countNoContacted}</Typography>
                                    <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 700 }}>CLIENTES</Typography>
                                </Box>
                            </Box>

                            <Stack spacing={2}>
                                <LegendItem color="#0051b3" label="Contactados" value={countContacted} />
                                <LegendItem color="#b39e00" label="No Contactados" value={countNoContacted} />
                                <Divider />
                                <Typography variant="caption" sx={{ color: theme.textSecondary, fontStyle: 'italic' }}>
                                    Última actualización: hace 5 min
                                </Typography>
                            </Stack>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

const StatCard = ({ title, count, percent, data, color, icon, labels }) => (
    <Grid item xs={12} sm={6} lg={4}>
        <Paper sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '20px',
            bgcolor: 'white',
            border: `1px solid rgba(226, 232, 240, 0.8)`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: { xs: 280, md: 340 },
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: color, transform: 'scale(1.2)' }}>{icon}</Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#64748b', fontSize: '1.2rem' }}>{title}</Typography>
                </Box>
                <Typography variant="body2" sx={{ bgcolor: `${color}15`, color: color, px: 2, py: 1, borderRadius: '12px', fontWeight: 800, fontSize: '1rem' }}>
                    {percent}%
                </Typography>
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 900, mt: 2 }}>{count}</Typography>

            <Box sx={{ height: 160, width: '100%', mt: 2 }}>
                <LineChart
                    series={[{ data: data, area: true, color: color, showMark: false }]}
                    xAxis={[{ scaleType: 'point', data: labels, hideTooltip: true }]}
                    leftAxis={null}
                    bottomAxis={null}
                    margin={{ top: 10, bottom: 0, left: -10, right: 0 }}
                    sx={{
                        '.MuiAreaElement-root': { fillOpacity: 0.15 },
                        '.MuiLineElement-root': { strokeWidth: 4 },
                    }}
                    slotProps={{ legend: { hidden: true } }}
                />
            </Box>
        </Paper>
    </Grid>
);

const LegendItem = ({ color, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
        <Box>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 600 }}>{label}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{value}</Typography>
        </Box>
    </Box>
);

export default PanelAdmin;